import React, { useEffect, useState } from "react";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { AiChat } from "@src/features/ai/components/AiChat/AiChat";
import { useRedirectOnCategorySwitch } from "@src/features/experiments/hooks/use-redirect-on-category-switch";
import { useSelector } from "@src/store";
import { getIsAiChatOpen, getIsShellRailHost } from "@src/store/slices/global";

import { AlertBannerProvider } from "./AlertBannerProvider";
import type { AppLayoutShellProps } from "./AppLayoutShell.types";
import { RootPageTopBarContext } from "./RootPageTopBar";

export type {
  AppLayoutShellMaxWidth,
  AppLayoutShellProps,
} from "./AppLayoutShell.types";

/**
 * Shared desktop shell for logged-in page providers.
 *
 * Guarantees:
 * - Optional `banner` and `sideNav` slots.
 * - Content width modes: centered `1600` container or full-width (`none`).
 * - Main scrolling remains document-level (no additional nested scroll regions).
 *
 * Provider-specific state and behavior should remain in the provider and be passed in via
 * slots/children.
 */
export const AppLayoutShell = ({
  banner,
  sideNav,
  maxWidth = 1600,
  children,
}: AppLayoutShellProps) => {
  const isAiChatOpen = useSelector(getIsAiChatOpen);
  // A full-page dialog can claim the rail (it is portalled above this shell, so
  // a rail rendered here would sit underneath it). Only one `AiChat` may be
  // mounted at a time — see `AiChat.railHost`.
  const isShellRailHost = useSelector(getIsShellRailHost);
  // DOM node in state, so `useState` rather than immer (drafts reject DOM
  // types). The slot's height feeds the chat rail's sticky offset, so the
  // rail starts below the bar a root page portals in — and collapses back to
  // the full viewport when the slot is empty.
  const [topBarNode, setTopBarNode] = useState<HTMLElement | null>(null);
  const [topBarHeight, setTopBarHeight] = useState(0);
  useEffect(() => {
    if (!topBarNode) {
      setTopBarHeight(0);
      return;
    }
    const observer = new ResizeObserver(() => {
      setTopBarHeight(topBarNode.offsetHeight);
    });
    observer.observe(topBarNode);
    return () => observer.disconnect();
  }, [topBarNode]);
  // Swaps route when the AI assistant flips category (experiment <->
  // personalization) on the experience currently on screen — the two
  // categories are separate route trees with no shared parent that would
  // otherwise pick up the change. Mounted here (not inside either route) so
  // it survives the swap and the open chat isn't disturbed by it.
  useRedirectOnCategorySwitch();
  const contentWidthStyles =
    maxWidth === 1600
      ? {
          maxWidth: "1600px",
          marginLeft: "auto",
          marginRight: "auto",
          width: "100%",
        }
      : {};

  return (
    <AlertBannerProvider>
      <Stack sx={{ minHeight: "100vh" }}>
        {banner ? <Box component="header">{banner}</Box> : null}
        <Stack direction="row" sx={{ flex: 1 }}>
          {sideNav ? (
            <Box
              component="nav"
              aria-label="Primary navigation"
              sx={{
                flexShrink: 0,
                position: "sticky",
                top: 0,
                height: "100dvh",
                overflowY: "auto",
                alignSelf: "flex-start",
              }}
            >
              {sideNav}
            </Box>
          ) : null}
          <Stack sx={{ flex: 1, minWidth: 0 }}>
            {/* Full-width top-bar slot: a root page (`IgRootPageWrapper`)
                portals its breadcrumbs/actions row here so the row spans the
                whole layout — including over the AI chat rail below. Empty
                (zero-height) on every other page. */}
            <Box ref={setTopBarNode} sx={{ flexShrink: 0 }} />
            <Stack direction="row" sx={{ flex: 1, minWidth: 0 }}>
              <Stack
                component="main"
                id="main-content"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  position: "relative",
                  ...contentWidthStyles,
                }}
              >
                <RootPageTopBarContext.Provider
                  value={{ node: topBarNode, height: topBarHeight }}
                >
                  {children}
                </RootPageTopBarContext.Provider>
              </Stack>
              {isAiChatOpen && isShellRailHost ? (
                <Box
                  component="aside"
                  aria-label="AI chat"
                  sx={{
                    flexShrink: 0,
                    position: "sticky",
                    top: topBarHeight,
                    height: `calc(100dvh - ${topBarHeight}px)`,
                    alignSelf: "flex-start",
                  }}
                >
                  <AiChat />
                </Box>
              ) : null}
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </AlertBannerProvider>
  );
};
